/* eslint-disable @typescript-eslint/no-unsafe-type-assertion */

import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import mitt from 'mitt'
import { t } from '@/utils/helpers/translate'

/**
 * @description Mixin to treat the discard changes modal
 *
 * Why use this @Mixin ?
 * This mixin provide some ways to control your discard changes modal,
 * also is a good option to prevent the repeated code around the application
 *
 * How to use this @Mixin
 * First, import this mixin in your component and declare it on the `mixins` attribute
 *
 * To start, you will need call the method @initDiscardChangesModal in your @created or @mounted component hook to init the discard changes modal
 * created () {
 *  this.initDiscardChangesModal()
 * }
 *
 * also you can pass your own initial attributes, that are the all options possible and their initial values
 * this.initDiscardChangesModal({
 *   opened: false,
 *   loading: false,
 *   customClass: '',
 *   testId: 'discard-changes-modal',
 *   type: 'caution',
 *   title: 'Discard changes?',
 *   content: `There are some changes in the story which are <br> not saved. Are you sure you want to discard the changes?`,
 *   buttonLabel: 'Discard changes',
 *   watchRoute: true,
 *   ignoredRoutesOnUpdate: [],
 * })
 *
 * also we have the @updateDiscardChangesModal method to update the initial attributes
 *
 * after that you will need to include the <DiscardChangesModal/> in your component template, without any params and the component order does not matter!
 *
 * How the mixin know if has changes?
 * for the mixin to know if has changes, we need a boolean coming from the computed property @discardChangesModalDiff, the suggestion is do a diff checking into changes then return a boolean
 *
 * When the modal will open?
 * when user try to leave the page by clicking on arrow back from browser or clicking in a route change method, the discard changes modal will open if @discardChangesModalDiff and also the @watchRoute are true
 *
 * But when a route update occurs (like changing only the parameters values) the @beforeRouteUpdate hook will be called instead of @beforeRouteLeave ,
 * and he will not try to open the modal if the target route are inside the ignoredRoutesOnUpdate attribute when @beforeRouteUpdate hook occurs
 *
 * also whe have the @watchRoute attribute which controlls if the mixin will watch the route change to show the modal or not, if disabled, the modal will need a manual trigger to be opened, in this mixin we have the methods
 * to treat this, the @handleDiscardChangesModalOpen method and the @handleDiscardChangesModalClose method
 *
 **/

type DiscardChangesModalAttributes = {
  id: string
  opened: boolean
  loading: boolean
  customClass: string
  testId: string
  type: string
  title: string
  content: string
  buttonLabel: string
  watchRoute: boolean
  ignoredRoutesOnUpdate: string[]
  ignoredRoutesOnLeave: string[]
  ignoredRouteQueryParamOnUpdate: string[]
  ignoredRouteQueryParamOnLeave: string[]
  allowParentNavigation: boolean
}

type DiscardChangesModalState = {
  attributes: DiscardChangesModalAttributes
  watchRouteTemporarilyDisabled: boolean
}

type DiscardChangesMixinInstance = {
  discardChangesPreventHandler(originalCallback: () => void): void
  discardChangesModal: DiscardChangesModalState
  watchRouteTemporarilyDisabled: boolean
  discardChangesModalDiff: boolean
  discardChangesModalWatchRoute: boolean
  discardChangesModalAllowParentNavigation: boolean
  discardChangesModalIgnoredRouteQueryParamOnUpdate: string[]
  discardChangesModalIgnoredRouteQueryParamOnLeave: string[]
  discardChangesModalIgnoredRoutesOnUpdate: string[]
  discardChangesModalIgnoredRoutesOnLeave: string[]
}

type DiscardChangesEvents = {
  discardChangesPrevent: () => void
  discardChangesModalClose: void
  discardChangesModalConfirm: void
  discardChangesModalDestroy: void
  discardChangesModalSync: Partial<DiscardChangesModalAttributes>
}

export const discardChangesEventBus = mitt<DiscardChangesEvents>()

const DEFAULT_ATTRIBUTES: DiscardChangesModalAttributes = {
  id: 'default',
  opened: false,
  loading: false,
  customClass: 'modal-delete-caution-confirm--hide-close-button',
  testId: 'discard-changes-modal',
  type: 'caution',
  title: t('Discard changes?'),
  content: t(
    `There are some changes which are not saved. Are you sure you want to discard the changes?`,
  ),
  buttonLabel: t('Discard changes'),
  watchRoute: true,
  ignoredRoutesOnUpdate: [],
  ignoredRoutesOnLeave: [],
  ignoredRouteQueryParamOnUpdate: [],
  ignoredRouteQueryParamOnLeave: [],
  allowParentNavigation: true,
}

export default {
  data(): { discardChangesModal: DiscardChangesModalState } {
    return {
      discardChangesModal: {
        attributes: { ...DEFAULT_ATTRIBUTES },
        watchRouteTemporarilyDisabled: false,
      },
    }
  },

  created(this: DiscardChangesMixinInstance) {
    discardChangesEventBus.on(
      'discardChangesPrevent',
      this.discardChangesPreventHandler,
    )
  },

  unmounted(this: DiscardChangesMixinInstance) {
    discardChangesEventBus.off(
      'discardChangesPrevent',
      this.discardChangesPreventHandler,
    )
  },

  computed: {
    discardChangesModalDiff() {
      return {}
    },

    discardChangesModalWatchRoute(this: DiscardChangesMixinInstance) {
      return this.discardChangesModal.attributes.watchRoute
    },

    discardChangesModalIgnoredRoutesOnUpdate(
      this: DiscardChangesMixinInstance,
    ) {
      return this.discardChangesModal.attributes.ignoredRoutesOnUpdate
    },

    discardChangesModalIgnoredRoutesOnLeave(this: DiscardChangesMixinInstance) {
      return this.discardChangesModal.attributes.ignoredRoutesOnLeave
    },

    discardChangesModalIgnoredRouteQueryParamOnUpdate(
      this: DiscardChangesMixinInstance,
    ) {
      return this.discardChangesModal.attributes.ignoredRouteQueryParamOnUpdate
    },

    discardChangesModalIgnoredRouteQueryParamOnLeave(
      this: DiscardChangesMixinInstance,
    ) {
      return this.discardChangesModal.attributes.ignoredRouteQueryParamOnLeave
    },

    discardChangesModalAllowParentNavigation(
      this: DiscardChangesMixinInstance,
    ) {
      return this.discardChangesModal.attributes.allowParentNavigation
    },
  },

  methods: {
    async discardChangesPromise() {
      const instance = this as unknown as DiscardChangesMixinInstance
      if (instance.discardChangesModalDiff) {
        instance.discardChangesModal.attributes.opened = true

        try {
          await new Promise((resolve, reject) => {
            discardChangesEventBus.on('discardChangesModalClose', () => {
              this.handleDiscardChangesModalClose()

              return reject(new Error())
            })

            discardChangesEventBus.on('discardChangesModalConfirm', () => {
              this.handleDiscardChangesModalConfirm()

              return resolve(undefined)
            })
          })
        } finally {
          discardChangesEventBus.off('discardChangesModalClose')
          discardChangesEventBus.off('discardChangesModalConfirm')
        }
      }
    },

    discardChangesPreventHandler(originalCallback: () => void): void {
      this.discardChangesPromise()
        .then(() => {
          const instance = this as unknown as DiscardChangesMixinInstance

          instance.watchRouteTemporarilyDisabled = true

          originalCallback()

          instance.watchRouteTemporarilyDisabled = false
        })
        .catch(() => {})
    },

    async discardChangesRouteMiddleware(
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ): Promise<void> {
      const instance = this as unknown as DiscardChangesMixinInstance

      const isRouteByPassed =
        to.meta.discardChangesByPass ||
        !instance.discardChangesModalWatchRoute ||
        instance.watchRouteTemporarilyDisabled

      const hasParentNavigation =
        instance.discardChangesModalAllowParentNavigation &&
        (to.fullPath.includes(from.fullPath) ||
          from.fullPath.includes(to.fullPath))

      if (isRouteByPassed || hasParentNavigation) {
        return next()
      }

      try {
        await this.discardChangesPromise()

        next()
      } catch {
        next(false)
      }
    },

    initDiscardChangesModal(
      discardChangesModalAttributes: DiscardChangesModalAttributes,
    ) {
      const instance = this as unknown as DiscardChangesMixinInstance

      const isSame =
        discardChangesModalAttributes.id ===
        instance.discardChangesModal.attributes.id

      const attributesToKeep = isSame
        ? instance.discardChangesModal.attributes
        : DEFAULT_ATTRIBUTES

      instance.discardChangesModal.attributes = {
        ...attributesToKeep,
        ...discardChangesModalAttributes,
      }

      // @ts-expect-error - this.$nextTick is not defined in the mixin
      this.$nextTick(() => {
        this.createComponent()
      })
    },

    destroyDiscardChangesModal() {
      const instance = this as unknown as DiscardChangesMixinInstance
      instance.discardChangesModal.attributes = { ...DEFAULT_ATTRIBUTES }
      discardChangesEventBus.emit('discardChangesModalDestroy')
      this.createComponent()
    },

    updateDiscardChangesModal(
      discardChangesModalAttributes: DiscardChangesModalAttributes,
    ) {
      const instance = this as unknown as DiscardChangesMixinInstance
      instance.discardChangesModal.attributes = {
        ...instance.discardChangesModal.attributes,
        ...discardChangesModalAttributes,
      }
    },

    createComponent() {
      const instance = this as unknown as DiscardChangesMixinInstance
      discardChangesEventBus.emit(
        'discardChangesModalSync',
        instance.discardChangesModal.attributes,
      )
    },

    handleDiscardChangesModalConfirm() {
      this.handleClose()
    },

    handleDiscardChangesModalOpen() {
      const instance = this as unknown as DiscardChangesMixinInstance
      instance.discardChangesModal.attributes.opened = true
    },

    handleDiscardChangesModalClose() {
      this.handleClose()
    },

    handleClose() {
      const instance = this as unknown as DiscardChangesMixinInstance
      instance.discardChangesModal.attributes.opened = false
      this.createComponent()
    },

    routeUpdatedByIgnoredParam(
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      ignoredParams: string[],
    ) {
      return Object.entries(from.query).some(([param, value]) => {
        const hasParamChanged = to.query[param] !== value
        return hasParamChanged && ignoredParams.includes(param)
      })
    },

    onRouteUpdate(
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) {
      const instance = this as unknown as DiscardChangesMixinInstance
      const ignoredParams =
        instance.discardChangesModalIgnoredRouteQueryParamOnUpdate

      if (
        instance.discardChangesModalIgnoredRoutesOnUpdate.includes(
          to.name as string,
        ) ||
        this.routeUpdatedByIgnoredParam(to, from, ignoredParams)
      ) {
        return next()
      }

      return this.discardChangesRouteMiddleware(to, from, next)
    },

    onRouteLeave(
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) {
      const instance = this as unknown as DiscardChangesMixinInstance
      const ignoredParams =
        instance.discardChangesModalIgnoredRouteQueryParamOnLeave

      if (
        instance.discardChangesModalIgnoredRoutesOnLeave.includes(
          to.name as string,
        ) ||
        this.routeUpdatedByIgnoredParam(to, from, ignoredParams)
      ) {
        return next()
      }

      return this.discardChangesRouteMiddleware(to, from, next)
    },
  },
}
